import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
/* Popover/Command rimossi — dropdown custom per evitare ResizeObserver loop */
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Phone, FileText, User, Stethoscope, CheckCircle2,
  Loader2, Building2, Heart, ClipboardList, ShieldCheck, Activity,
  Pill, CalendarDays, Clock, Users, Zap, Lock, X, ChevronsUpDown, Check,
  AlertTriangle, RefreshCw, Briefcase, Target, Calendar as CalendarIcon,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { getModello431 } from '@/data/modello431Data';
import { paiActiveData } from '@/data/mockData';
import PatientPanelHeader from '@/components/PatientPanelHeader';
import { toast } from 'sonner';
import { it } from 'date-fns/locale';

/* ═══ Mock: Equipe & Availability ═══ */
const equipeProfessionals = [
  // Nucleo Base (Obbligatori)
  { id: 'medico_distretto', nome: 'Dr. Franco Lombardi', ruolo: 'Medico di Distretto (Responsabile Clinico)', group: 'base' },
  { id: 'mmg_pls', nome: 'Dr. Alessandro Bianchi', ruolo: 'MMG / Pediatra (PLS)', group: 'base' },
  { id: 'infermiere_cm', nome: 'Dott.ssa Chiara Galli', ruolo: 'Infermiere Case Manager', group: 'base' },
  { id: 'assistente_sociale', nome: 'Dott.ssa Paola Ferretti', ruolo: 'Assistente Sociale', group: 'base' },
  // Specialisti (Attivabili al bisogno)
  { id: 'geriatra', nome: 'Dr. Mario Rossi', ruolo: 'Geriatra', group: 'specialisti' },
  { id: 'fisiatra', nome: 'Dr. Roberto Santoro', ruolo: 'Fisiatra', group: 'specialisti' },
  { id: 'neurologo', nome: 'Dott.ssa Sofia Gatti', ruolo: 'Neurologo', group: 'specialisti' },
  { id: 'psichiatra', nome: 'Dr. Enrico De Santis', ruolo: 'Psichiatra', group: 'specialisti' },
  { id: 'palliativista', nome: 'Dott.ssa Elena Moretti', ruolo: 'Palliativista', group: 'specialisti' },
  { id: 'psicologo', nome: 'Dott.ssa Anna Verdi', ruolo: 'Psicologo', group: 'specialisti' },
  { id: 'fisioterapista', nome: 'Dott.ssa Laura Conti', ruolo: 'Fisioterapista', group: 'specialisti' },
];

function generateAvailableSlots(selectedCount) {
  const today = new Date();
  const slots = {};
  const allTimes = ['09:00', '10:00', '11:00', '14:30', '15:30'];
  // Scarsità: solo 3-4 giorni disponibili nel mese (realistico per incrocio agende)
  const sparseOffsets = [5, 9, 14, 19];
  const maxDays = selectedCount >= 3 ? 3 : 4;
  const usedOffsets = sparseOffsets.slice(0, maxDays);
  for (const d of usedOffsets) {
    const date = new Date(today);
    date.setDate(today.getDate() + d);
    if (date.getDay() === 0) date.setDate(date.getDate() + 1);
    if (date.getDay() === 6) date.setDate(date.getDate() + 2);
    const dateKey = date.toISOString().split('T')[0];
    const numSlots = Math.max(2, 5 - selectedCount);
    const startIdx = (d * 2) % allTimes.length;
    const daySlots = [];
    for (let i = 0; i < numSlots && i < allTimes.length; i++) {
      daySlots.push(allTimes[(startIdx + i) % allTimes.length]);
    }
    slots[dateKey] = [...new Set(daySlots)].sort();
  }
  return slots;
}

/* ═══ Reusable Sub-components ═══ */
function ReadOnlyField({ label, value, wide }) {
  return (
    <div className={wide ? 'sm:col-span-2 lg:col-span-3' : ''}>
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
      <div className="text-sm text-foreground font-medium bg-muted/50 rounded-lg px-3 py-2 min-h-[36px] border border-border/40">
        {value || <span className="text-muted-foreground italic">—</span>}
      </div>
    </div>
  );
}

function ReadOnlyCheckbox({ label, checked }) {
  return (
    <div className="flex items-center gap-2.5">
      <Checkbox checked={checked} disabled className="data-[state=checked]:bg-primary data-[state=checked]:border-primary opacity-70" />
      <Label className="text-sm text-muted-foreground font-normal leading-tight">{label}</Label>
    </div>
  );
}

function ReadOnlyRadio({ value, label }) {
  return (
    <div className="flex items-center gap-2">
      <RadioGroupItem value={value} id={`ro_${value}_${label}`} disabled />
      <Label htmlFor={`ro_${value}_${label}`} className="text-sm font-normal text-muted-foreground">{label}</Label>
    </div>
  );
}

function SectionHeading({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 mb-3 mt-1">
      {Icon && <Icon className="w-4 h-4 text-primary flex-shrink-0" />}
      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{children}</h4>
    </div>
  );
}

/* ═══ PAI Helpers ═══ */
const statoLabelsDettaglio = {
  da_accettare: 'Da Accettare',
  presa_in_carico: 'Presa in Carico',
  in_valutazione: 'In Valutazione',
  pai_completato: 'PAI Completato',
  assegnazione: 'In Assegnazione',
};

function parseDateIT(str) {
  if (!str) return null;
  const parts = str.split('/');
  if (parts.length !== 3) return null;
  return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
}

function getDaysUntilExpiry(dataFinePAI) {
  const endDate = parseDateIT(dataFinePAI);
  if (!endDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
export default function DettaglioRichiesta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { patients, acceptRequest } = useApp();

  const [selectedEquipe, setSelectedEquipe] = useState([]);
  const [equipeOpen, setEquipeOpen] = useState(false);
  const [equipeSearch, setEquipeSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isCalling, setIsCalling] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [assignedProvider, setAssignedProvider] = useState(null);
  const equipeDropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (equipeDropdownRef.current && !equipeDropdownRef.current.contains(e.target)) {
        setEquipeOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const patient = useMemo(() => patients.find(p => p.id === parseInt(id)), [patients, id]);
  const m431 = useMemo(() => patient ? getModello431(patient.id, patient) : null, [patient]);

  const isMonitoraggio = patient && patient.stato !== 'da_accettare';
  const paiData = patient ? paiActiveData[patient.id] : null;
  const daysUntilExpiry = paiData ? getDaysUntilExpiry(paiData.dataFinePAI) : null;
  const isPAIExpiring = daysUntilExpiry !== null && daysUntilExpiry >= 0 && daysUntilExpiry <= 15;

  const availableSlots = useMemo(() => {
    if (selectedEquipe.length === 0) return {};
    return generateAvailableSlots(selectedEquipe.length);
  }, [selectedEquipe]);

  const availableDates = useMemo(() => {
    return Object.keys(availableSlots).map(d => new Date(d + 'T00:00:00'));
  }, [availableSlots]);

  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    const key = selectedDate.toISOString().split('T')[0];
    return availableSlots[key] || [];
  }, [selectedDate, availableSlots]);

  const canAccept = selectedEquipe.length > 0 && selectedSlot !== null;
  const namesRevealed = selectedSlot !== null;

  if (!patient || !m431) {
    return (
      <AppLayout role="segreteria">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Paziente non trovato</p>
        </div>
      </AppLayout>
    );
  }

  const provenienzaLabels = { mmg: "MMG/PLS", ospedale: "Ospedale (Dim. Protetta)", struttura: "Struttura Resid.", altro: "Altro" };

  const handleEquipeToggle = (profId) => {
    setSelectedEquipe(prev => prev.includes(profId) ? prev.filter(x => x !== profId) : [...prev, profId]);
    setSelectedDate(undefined);
    setSelectedSlot(null);
  };

  const handleCall = () => {
    setIsCalling(true);
    setTimeout(() => {
      setIsCalling(false);
      toast.success('Chiamata simulata', { description: `Contattato ${patient.cognome} ${patient.nome} al ${patient.telefono}` });
    }, 1500);
  };

  const handleAccept = () => {
    if (!canAccept) return;
    setIsAccepting(true);
    const equipeArray = selectedEquipe.map(x => {
      const p = equipeProfessionals.find(pr => pr.id === x);
      return p ? { nome: p.nome, ruolo: p.ruolo } : null;
    }).filter(Boolean);
    const equipeLabels = equipeArray.map(e => `${e.nome} - ${e.ruolo}`).join(', ');
    const dateStr = selectedDate?.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
    setTimeout(() => {
      acceptRequest(patient.id, equipeLabels, `${dateStr} ore ${selectedSlot}`, equipeArray);
      setIsAccepting(false);
      toast.success('Richiesta accettata e attivata', { description: `Visita UVMD fissata per ${dateStr} alle ${selectedSlot}.` });
      setTimeout(() => navigate('/segreteria'), 1200);
    }, 1800);
  };

  /* ═══ Provider convenzionati (bassa complessità) ═══ */
  const providerConvenzionati = [
    { id: 'prov-1', nome: 'Cooperativa Sociale "Cuore di San Camillo"', distanza: '1.2 km', punteggio: 92, specializzazioni: 'Assistenza infermieristica domiciliare, fisioterapia riabilitativa, OSS' },
    { id: 'prov-2', nome: 'Consorzio "Il Mantello di San Martino"', distanza: '2.8 km', punteggio: 88, specializzazioni: 'Infermieristica territoriale, logopedia, supporto socio-sanitario' },
    { id: 'prov-3', nome: 'Ente Assistenziale "Sollievo del Cireneo"', distanza: '3.5 km', punteggio: 95, specializzazioni: 'Cure domiciliari integrate, fisioterapia, nutrizione clinica' },
    { id: 'prov-4', nome: 'Cooperativa "Luce di Santa Rita"', distanza: '4.1 km', punteggio: 90, specializzazioni: 'Infermieristica specializzata, fisioterapia neurologica, logopedia' },
  ];

  const handleConfirmProvider = () => {
    if (!selectedProvider) return;
    const prov = providerConvenzionati.find(p => p.id === selectedProvider);
    setAssignedProvider(prov);
    setProviderModalOpen(false);
    setSelectedProvider(null);
    toast.success('Provider assegnato', { description: `${prov.nome} è stato notificato. Attesa accettazione entro 24h.` });
  };

  const isDateAvailable = (date) => {
    const key = date.toISOString().split('T')[0];
    return !!availableSlots[key];
  };

  const handleRivalutazione = () => {
    toast.success('Rivalutazione programmata', {
      description: `Nuovo ciclo UVMD avviato per ${patient.cognome} ${patient.nome}. L'equipe sara convocata a breve.`,
    });
  };

  /* ═══════════════════════════════════════
     MONITORAGGIO PAI VIEW
     ═══════════════════════════════════════ */
  if (isMonitoraggio) {
    // Calculate progress bar
    const startDate = paiData ? parseDateIT(paiData.dataInizioPAI) : null;
    const endDate = paiData ? parseDateIT(paiData.dataFinePAI) : null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const totalDays = startDate && endDate ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) : 0;
    const elapsedDays = startDate ? Math.max(0, Math.ceil((today - startDate) / (1000 * 60 * 60 * 24))) : 0;
    const progressPct = totalDays > 0 ? Math.min(100, Math.round((elapsedDays / totalDays) * 100)) : 0;
    const startLabel = paiData?.dataInizioPAI?.substring(0, 5) || '';
    const endLabel = paiData?.dataFinePAI?.substring(0, 5) || '';

    return (
      <AppLayout role="segreteria">
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center gap-4 flex-wrap">
            <Button variant="ghost" size="icon" onClick={() => navigate('/segreteria')} className="text-muted-foreground flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl font-semibold text-foreground">{patient.cognome} {patient.nome}</h1>
                <Badge variant="success" className="text-[11px] gap-1" data-testid="badge-stato-attivo">
                  <CheckCircle2 className="w-3 h-3" />
                  ATTIVO — {statoLabelsDettaglio[patient.stato] || patient.stato}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{patient.codiceFiscale} | {m431.mmg.distretto}</p>
            </div>
            <Button variant="comfort-outline" size="sm" className="gap-1.5 flex-shrink-0" onClick={handleCall} disabled={isCalling}>
              {isCalling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
              {isCalling ? 'Chiamata...' : 'Chiama Paziente'}
            </Button>
          </div>

          {/* 2-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] xl:grid-cols-[1fr_420px] gap-6 items-start">

            {/* ════════ LEFT COLUMN: Tabs ════════ */}
            <div className="min-w-0">
              <Tabs defaultValue="pai" className="w-full">
                <TabsList className="w-full grid grid-cols-2 mb-5" data-testid="monitoraggio-tabs">
                  <TabsTrigger value="pai" className="text-xs sm:text-sm" data-testid="tab-pai">
                    <ClipboardList className="w-3.5 h-3.5 mr-1.5" />
                    Piano Assistenziale (PAI)
                  </TabsTrigger>
                  <TabsTrigger value="richiesta" className="text-xs sm:text-sm" data-testid="tab-richiesta">
                    <FileText className="w-3.5 h-3.5 mr-1.5" />
                    Dati Richiesta Originale
                  </TabsTrigger>
                </TabsList>

                {/* ── TAB 1: Piano Assistenziale ── */}
                <TabsContent value="pai" className="space-y-5 mt-0" data-testid="tab-content-pai">

                  {/* Timeline PAI Progress Bar */}
                  {paiData && (
                    <div className="bg-card border border-border rounded-xl p-5 shadow-card" data-testid="pai-timeline">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                          <CalendarDays className="w-4 h-4 text-primary" />
                          Timeline PAI
                        </h3>
                        <span className="text-xs font-semibold text-muted-foreground" data-testid="pai-day-counter">
                          Giorno {Math.min(elapsedDays, totalDays)} di {totalDays}
                        </span>
                      </div>
                      <div className="relative">
                        {/* Progress track */}
                        <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progressPct}%` }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                            className={`h-full rounded-full ${progressPct >= 85 ? 'bg-warning' : 'bg-primary'}`}
                            data-testid="pai-progress-bar"
                          />
                        </div>
                        {/* Labels */}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[11px] font-medium text-muted-foreground">Inizio: {startLabel}</span>
                          <span className="text-xs font-bold text-foreground">{progressPct}%</span>
                          <span className={`text-[11px] font-medium ${isPAIExpiring ? 'text-warning font-semibold' : 'text-muted-foreground'}`}>Fine: {endLabel}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Warning Banner (contained in left column) */}
                  {isPAIExpiring && paiData && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/30 rounded-xl"
                      data-testid="pai-expiring-warning"
                    >
                      <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">PAI in scadenza il {paiData.dataFinePAI}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Necessaria rivalutazione UVMD prima della scadenza per garantire la continuita assistenziale.</p>
                      </div>
                    </motion.div>
                  )}

                  {/* Accordions */}
                  <Accordion type="multiple" defaultValue={["sintesi_pai", "anagrafica"]} className="space-y-4">
                    {/* Sintesi PAI Attuale */}
                    <AccordionItem value="sintesi_pai" className="border rounded-xl bg-card shadow-card overflow-hidden">
                      <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                            <Target className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-sm font-semibold text-foreground">Sintesi PAI Attuale</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-6">
                        <SectionHeading icon={Stethoscope}>Diagnosi Principale</SectionHeading>
                        <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2.5 border border-border/40 leading-relaxed mb-5">
                          {patient.diagnosiPrincipale}
                        </div>
                        {patient.diagnosiSecondaria && (
                          <>
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Diagnosi Concomitante</p>
                            <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2.5 border border-border/40 leading-relaxed mb-5">
                              {patient.diagnosiSecondaria}
                            </div>
                          </>
                        )}
                        <Separator className="my-4" />
                        <SectionHeading icon={Activity}>Obiettivi di Salute</SectionHeading>
                        {paiData?.obiettiviPAI ? (
                          <div className="flex flex-col gap-5">
                            {paiData.obiettiviPAI.map((obj, i) => (
                              <div key={i} className="flex items-start gap-3 p-3.5 bg-muted/30 rounded-lg border border-border/40" data-testid={`pai-obiettivo-${i}`}>
                                <div className="w-6 h-6 rounded-full bg-primary-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <span className="text-[10px] font-bold text-primary">{i + 1}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-primary uppercase tracking-wider">{obj.area}</p>
                                  <p className="text-sm text-foreground mt-0.5">{obj.obiettivo}</p>
                                  <p className="text-[11px] text-muted-foreground mt-1">Indicatore: {obj.indicatore}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">Nessun obiettivo PAI definito.</p>
                        )}
                      </AccordionContent>
                    </AccordionItem>

                    {/* Anagrafica */}
                    <AccordionItem value="anagrafica" className="border rounded-xl bg-card shadow-card overflow-hidden">
                      <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                            <User className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-sm font-semibold text-foreground">Dati Anagrafici e Contesto</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-6">
                        <SectionHeading icon={User}>Dati del Paziente</SectionHeading>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mb-4">
                          <ReadOnlyField label="Cognome" value={m431.anagrafica.cognome} />
                          <ReadOnlyField label="Nome" value={m431.anagrafica.nome} />
                          <ReadOnlyField label="Data di Nascita" value={m431.anagrafica.dataNascita} />
                          <ReadOnlyField label="Codice Fiscale" value={m431.anagrafica.codiceFiscale} />
                          <ReadOnlyField label="Telefono" value={m431.anagrafica.telefono} />
                          <ReadOnlyField label="Comune" value={m431.anagrafica.comuneResidenza} />
                        </div>
                        {patient.caregiver?.presente && (
                          <>
                            <Separator className="my-3" />
                            <SectionHeading icon={Heart}>Caregiver</SectionHeading>
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5">
                              <ReadOnlyField label="Nome" value={patient.caregiver.nome} />
                              <ReadOnlyField label="Relazione" value={patient.caregiver.relazione} />
                              <ReadOnlyField label="Telefono" value={patient.caregiver.telefono} />
                            </div>
                          </>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </TabsContent>

                {/* ── TAB 2: Dati Richiesta Originale (Modello 431 — Mirror Esatto della Vista Triage) ── */}
                <TabsContent value="richiesta" className="space-y-3 mt-0" data-testid="tab-content-richiesta" id="modello-431-section">
                  {/* Read-Only Banner */}
                  <div className="bg-muted/50 border border-border rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold text-foreground">Modello 431 — Modulo Unico di Attivazione PUA/CAD</span>
                      <Badge variant="secondary" className="text-[9px]">Sola Lettura</Badge>
                    </div>
                    <Badge variant="chip" className="text-[9px]">ASL Roma | Regione Lazio</Badge>
                  </div>

                  <Accordion type="multiple" defaultValue={["acc_anagrafica", "acc_sanitaria", "acc_sociale", "acc_percorso"]} className="space-y-3">

                    {/* ── SEZ. 1: ANAGRAFICA (Mirror Esatto) ── */}
                    <AccordionItem value="acc_anagrafica" className="border rounded-xl bg-card shadow-card overflow-hidden" data-testid="acc-anagrafica">
                      <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                            <User className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-sm font-semibold text-foreground">Sezione 1 — Dati Anagrafici e Contesto</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-5">
                        <SectionHeading icon={User}>Dati del Paziente</SectionHeading>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mb-4">
                          <ReadOnlyField label="Cognome" value={m431.anagrafica.cognome} />
                          <ReadOnlyField label="Nome" value={m431.anagrafica.nome} />
                          <ReadOnlyField label="Data di Nascita" value={m431.anagrafica.dataNascita} />
                          <ReadOnlyField label="Luogo di Nascita" value={m431.anagrafica.luogoNascita} />
                          <ReadOnlyField label="Codice Fiscale" value={m431.anagrafica.codiceFiscale} />
                          <ReadOnlyField label="Telefono" value={m431.anagrafica.telefono} />
                          <ReadOnlyField label="Via" value={m431.anagrafica.via} />
                          <ReadOnlyField label="N. Civico" value={m431.anagrafica.civico} />
                          <ReadOnlyField label="CAP" value={m431.anagrafica.cap} />
                          <ReadOnlyField label="Comune" value={m431.anagrafica.comuneResidenza} />
                          <ReadOnlyField label="Municipio" value={m431.anagrafica.municipio} />
                          <ReadOnlyField label="Domicilio (se diverso)" value={m431.anagrafica.domicilio || "Coincide con residenza"} />
                        </div>
                        <Separator className="my-3" />
                        <SectionHeading icon={Heart}>Persona di Riferimento</SectionHeading>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
                          <ReadOnlyField label="Relazione" value={m431.personaRiferimento.relazione} />
                          <ReadOnlyField label="Cognome" value={m431.personaRiferimento.cognome} />
                          <ReadOnlyField label="Nome" value={m431.personaRiferimento.nome} />
                          <ReadOnlyField label="Telefono" value={m431.personaRiferimento.telefono} />
                        </div>
                        <Separator className="my-3" />
                        <SectionHeading icon={Stethoscope}>Medico Curante (MMG)</SectionHeading>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-4">
                          <ReadOnlyField label="Cognome" value={m431.mmg.cognome} />
                          <ReadOnlyField label="Nome" value={m431.mmg.nome} />
                          <ReadOnlyField label="Distretto" value={m431.mmg.distretto} />
                        </div>
                        <Separator className="my-3" />
                        <SectionHeading icon={Building2}>Provenienza Richiesta</SectionHeading>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                          {Object.entries(provenienzaLabels).map(([key, label]) => (
                            <ReadOnlyCheckbox key={key} label={label} checked={m431.provenienza === key} />
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* ── SEZ. 2: VALUTAZIONE SANITARIA (Mirror Esatto) ── */}
                    <AccordionItem value="acc_sanitaria" className="border rounded-xl bg-card shadow-card overflow-hidden" data-testid="acc-sanitaria">
                      <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                            <Stethoscope className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-sm font-semibold text-foreground">Sezione 2 — Valutazione Sanitaria</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-5">
                        <SectionHeading icon={ClipboardList}>Diagnosi</SectionHeading>
                        <div className="space-y-2.5 mb-4">
                          <div>
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Diagnosi Principale</p>
                            <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40 leading-relaxed">{m431.diagnosi.principale}</div>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Concomitanti</p>
                            <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40 leading-relaxed">{m431.diagnosi.concomitanti}</div>
                          </div>
                          <div>
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Codici ICD-9</p>
                            <div className="inline-flex flex-wrap gap-1.5">
                              {(m431.diagnosi.codiciICD9 || '').split(',').filter(Boolean).map((code, i) => (
                                <Badge key={i} variant="chip" className="text-xs font-mono">{code.trim()}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Evento acuto (90gg)?</p>
                            <RadioGroup value={m431.eventoAcuto90gg ? "si" : "no"} disabled className="flex gap-3">
                              <ReadOnlyRadio value="si" label="Si" />
                              <ReadOnlyRadio value="no" label="No" />
                            </RadioGroup>
                          </div>
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Trasportabile?</p>
                            <RadioGroup value={m431.trasportabile ? "si" : "no"} disabled className="flex gap-3">
                              <ReadOnlyRadio value="si" label="Si" />
                              <ReadOnlyRadio value="no" label="No" />
                            </RadioGroup>
                          </div>
                        </div>
                        <Separator className="my-3" />
                        <SectionHeading icon={Activity}>Condizioni Cliniche</SectionHeading>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 mb-4">
                          <ReadOnlyCheckbox label="Insuff. respiratoria" checked={m431.condizioniCliniche.insufficienzaRespiratoria} />
                          <ReadOnlyCheckbox label="Diabete" checked={m431.condizioniCliniche.diabete} />
                          <ReadOnlyCheckbox label="Cardiopatia" checked={m431.condizioniCliniche.cardiopatia} />
                          <ReadOnlyCheckbox label="Insuff. cardiaca" checked={m431.condizioniCliniche.insufficienzaCardiaca} />
                          <ReadOnlyCheckbox label="Malattie vascolari" checked={m431.condizioniCliniche.malattieVascolari} />
                          <ReadOnlyCheckbox label="Esiti pat. ortopediche" checked={m431.condizioniCliniche.esitiPatologieOrtopediche} />
                          <ReadOnlyCheckbox label="Mal. osteomuscolari" checked={m431.condizioniCliniche.malattieOsteomuscolari} />
                          <ReadOnlyCheckbox label="Esiti frattura femore" checked={m431.condizioniCliniche.esitiFratturaFemore} />
                          <ReadOnlyCheckbox label="Neurologiche" checked={m431.condizioniCliniche.neurologiche} />
                          <ReadOnlyCheckbox label="Mal. psichiatrica" checked={m431.condizioniCliniche.malattiaPsichiatrica} />
                          <ReadOnlyCheckbox label="Esiti di ictus" checked={m431.condizioniCliniche.esitiIctus} />
                          <ReadOnlyCheckbox label="Demenza" checked={m431.condizioniCliniche.demenza} />
                          <ReadOnlyCheckbox label="Disturbi sensoriali" checked={m431.condizioniCliniche.disturbiSensoriali} />
                          <ReadOnlyCheckbox label="Neoplasie" checked={m431.condizioniCliniche.neoplasie} />
                          <ReadOnlyCheckbox label="Altro" checked={m431.condizioniCliniche.altro} />
                        </div>
                        <Separator className="my-3" />
                        <SectionHeading icon={Pill}>Terapie in Atto</SectionHeading>
                        <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40 leading-relaxed mb-4">{m431.terapiaInAtto || <span className="text-muted-foreground italic">—</span>}</div>
                        <Separator className="my-3" />
                        <SectionHeading>Condizioni Cliniche Attuali</SectionHeading>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Stabilita Clinica</p>
                            <RadioGroup value={m431.stabilitaClinica} disabled className="space-y-1">
                              <ReadOnlyRadio value="stabile" label="Stabile" />
                              <ReadOnlyRadio value="moderatamente_instabile" label="Mod. instabile" />
                              <ReadOnlyRadio value="instabile" label="Instabile" />
                            </RadioGroup>
                          </div>
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Stato di Coscienza</p>
                            <RadioGroup value={m431.statoCoscienza} disabled className="space-y-1">
                              <ReadOnlyRadio value="lucido" label="Lucido" />
                              <ReadOnlyRadio value="confuso" label="Confuso" />
                              <ReadOnlyRadio value="soporoso" label="Soporoso" />
                              <ReadOnlyRadio value="coma" label="Coma" />
                            </RadioGroup>
                          </div>
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Disturbi Cognitivi</p>
                            <RadioGroup value={m431.disturbiCognitivi} disabled className="flex flex-wrap gap-x-3 gap-y-1">
                              <ReadOnlyRadio value="assenti" label="Assenti" />
                              <ReadOnlyRadio value="lievi" label="Lievi" />
                              <ReadOnlyRadio value="moderati" label="Moderati" />
                              <ReadOnlyRadio value="gravi" label="Gravi" />
                            </RadioGroup>
                          </div>
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Disturbi Comportam.</p>
                            <RadioGroup value={m431.disturbiComportamentali} disabled className="flex flex-wrap gap-x-3 gap-y-1">
                              <ReadOnlyRadio value="assenti" label="Assenti" />
                              <ReadOnlyRadio value="lievi" label="Lievi" />
                              <ReadOnlyRadio value="moderati" label="Moderati" />
                              <ReadOnlyRadio value="gravi" label="Gravi" />
                            </RadioGroup>
                          </div>
                        </div>
                        <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Scala del Dolore (0-10)</p>
                          <div className="flex items-center gap-3">
                            <span className="text-[10px] text-muted-foreground font-mono">0</span>
                            <Slider defaultValue={[m431.dolore]} max={10} step={1} disabled className="flex-1" />
                            <span className="text-[10px] text-muted-foreground font-mono">10</span>
                            <Badge variant={m431.dolore <= 3 ? 'success' : m431.dolore <= 6 ? 'warning' : 'alta'} className="text-[10px] font-mono min-w-[28px] justify-center">{m431.dolore}</Badge>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* ── SEZ. 3: VALUTAZIONE SOCIALE (Mirror Esatto) ── */}
                    <AccordionItem value="acc_sociale" className="border rounded-xl bg-card shadow-card overflow-hidden" data-testid="acc-sociale">
                      <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                            <Heart className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-sm font-semibold text-foreground">Sezione 3 — Valutazione Sociale e Funzionale</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-5">
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Mobilita</p>
                            <RadioGroup value={m431.mobilita} disabled className="space-y-1">
                              <ReadOnlyRadio value="indipendente" label="Indipendente" />
                              <ReadOnlyRadio value="aiuto_persona" label="Con aiuto" />
                              <ReadOnlyRadio value="totalmente_dipendente" label="Tot. dipendente" />
                            </RadioGroup>
                          </div>
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Alimentazione</p>
                            <RadioGroup value={m431.alimentazione} disabled className="space-y-1">
                              <ReadOnlyRadio value="autonoma" label="Autonoma" />
                              <ReadOnlyRadio value="assistito" label="Assistito" />
                              <ReadOnlyRadio value="rifiuta" label="Rifiuta" />
                              <ReadOnlyRadio value="artificiale" label="Artificiale" />
                            </RadioGroup>
                          </div>
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Vive con:</p>
                            <RadioGroup value={m431.supportoSociale} disabled className="space-y-1">
                              <ReadOnlyRadio value="solo" label="Solo" />
                              <ReadOnlyRadio value="coniuge" label="Coniuge" />
                              <ReadOnlyRadio value="con_figli" label="Figli" />
                              <ReadOnlyRadio value="genitori" label="Genitori" />
                            </RadioGroup>
                          </div>
                          <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Condizione Abitativa</p>
                            <RadioGroup value={m431.condizioneAbitativa} disabled className="space-y-1">
                              <ReadOnlyRadio value="idonea" label="Idonea" />
                              <ReadOnlyRadio value="parzialmente_idonea" label="Parz. Idonea" />
                              <ReadOnlyRadio value="non_idonea" label="Non Idonea" />
                            </RadioGroup>
                            {m431.barriereArchitettoniche && <p className="text-[10px] text-muted-foreground mt-1.5 italic">{m431.barriereArchitettoniche}</p>}
                          </div>
                        </div>
                        <SectionHeading icon={Activity}>Scala ADL</SectionHeading>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                          <ReadOnlyCheckbox label="Allettato" checked={m431.scalaADL.allettato} />
                          <ReadOnlyCheckbox label="Carrozzina" checked={m431.scalaADL.spostaCarrozzina} />
                          <ReadOnlyCheckbox label="Deambula con aiuto" checked={m431.scalaADL.deambulaConAiuto} />
                          <ReadOnlyCheckbox label="Confuso/Disorientato" checked={m431.scalaADL.confusoDisorientato} />
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* ── SEZ. 4: NECESSITA ASSISTENZIALI E PERCORSO (Mirror Esatto) ── */}
                    <AccordionItem value="acc_percorso" className="border rounded-xl bg-card shadow-card overflow-hidden" data-testid="acc-percorso">
                      <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                            <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-sm font-semibold text-foreground">Sezione 4 — Necessita Assistenziali e Percorso</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-5">
                        <SectionHeading>Trattamenti e Procedure</SectionHeading>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 mb-4">
                          <ReadOnlyCheckbox label="Chemio/Radioterapia" checked={m431.trattamentiProcedure.chemioRadioterapia} />
                          <ReadOnlyCheckbox label="Ossigenoterapia (OLT)" checked={m431.trattamentiProcedure.ossigenoterapiaOLT} />
                          <ReadOnlyCheckbox label="Ventilazione meccanica" checked={m431.trattamentiProcedure.ventilazioneMeccanica} />
                          <ReadOnlyCheckbox label="Tracheostomia" checked={m431.trattamentiProcedure.tracheostomia} />
                          <ReadOnlyCheckbox label="Broncoaspirazione" checked={m431.trattamentiProcedure.broncoaspirazione} />
                          <ReadOnlyCheckbox label="Drenaggio posturale" checked={m431.trattamentiProcedure.drenaggioPosturale} />
                          <ReadOnlyCheckbox label="Dialisi" checked={m431.trattamentiProcedure.dialisi} />
                          <ReadOnlyCheckbox label="Trasfusioni" checked={m431.trattamentiProcedure.trasfusioniPeriodiche} />
                        </div>
                        <Separator className="my-3" />
                        <SectionHeading>Presidi e Farmaci</SectionHeading>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 mb-4">
                          <ReadOnlyCheckbox label="Nutr. parenterale" checked={m431.somministrazioneFarmaci.nutrizioneParenterale} />
                          <ReadOnlyCheckbox label="Nutr. SNG" checked={m431.somministrazioneFarmaci.nutrizioneEnteraleSNG} />
                          <ReadOnlyCheckbox label="Nutr. PEG" checked={m431.somministrazioneFarmaci.nutrizioneEnteralePEG} />
                          <ReadOnlyCheckbox label="Catetere vescicale" checked={m431.somministrazioneFarmaci.catetereVescicale} />
                          <ReadOnlyCheckbox label="CVC o PICC" checked={m431.somministrazioneFarmaci.cvcPicc} />
                          <ReadOnlyCheckbox label="Enterostomia" checked={m431.somministrazioneFarmaci.enterostomia} />
                          <ReadOnlyCheckbox label="Ulcere cutanee" checked={m431.somministrazioneFarmaci.ulcereCutanee} />
                          <ReadOnlyCheckbox label="Terapia infusionale" checked={m431.somministrazioneFarmaci.terapiaInfusionale} />
                          <ReadOnlyCheckbox label="ECG periodici" checked={m431.somministrazioneFarmaci.ecgPeriodici} />
                          <ReadOnlyCheckbox label="Prelievi ematici" checked={m431.somministrazioneFarmaci.prelieviEmatici} />
                        </div>
                        <Separator className="my-3" />
                        <SectionHeading icon={FileText}>Percorso e Compilatore</SectionHeading>
                        <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40 mb-3">
                          <RadioGroup value={m431.percorsoRichiesto} disabled className="grid grid-cols-2 lg:grid-cols-3 gap-1.5">
                            <ReadOnlyRadio value="riab_residenziale" label="Riab. Resid." />
                            <ReadOnlyRadio value="riab_semiresidenziale" label="Riab. Semires." />
                            <ReadOnlyRadio value="riab_domiciliare" label="Riab. Domic." />
                            <ReadOnlyRadio value="adi" label="ADI" />
                            <ReadOnlyRadio value="rsa" label="RSA" />
                          </RadioGroup>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                          <ReadOnlyField label="Ruolo" value={m431.compilatore.ruolo === 'mmg' ? 'MMG' : 'Medico Osped.'} />
                          <ReadOnlyField label="Nominativo" value={`${m431.compilatore.cognome} ${m431.compilatore.nome}`} />
                          <ReadOnlyField label="Telefono" value={m431.compilatore.telefono} />
                          <ReadOnlyField label="Data" value={m431.compilatore.data} />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </TabsContent>
              </Tabs>
            </div>

            {/* ════════ RIGHT COLUMN: Services & Actions ════════ */}
            <div className="lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto rounded-2xl lg:bg-muted/20 lg:border lg:border-border/60 lg:p-5 flex flex-col gap-8">

              {/* Servizi Attivi */}
              <div className="bg-card rounded-xl border border-border shadow-card p-5">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-4">
                  <Briefcase className="w-4 h-4 text-primary" />
                  Servizi Attivi
                </h3>
                {paiData?.serviziAttivi ? (
                  <div className="flex flex-col gap-4" data-testid="servizi-attivi-list">
                    {paiData.serviziAttivi.map((servizio, i) => (
                      <div key={i} className="p-4 bg-card rounded-xl border border-border shadow-sm" data-testid={`servizio-card-${i}`}>
                        <p className="text-sm font-semibold text-foreground mb-2">{servizio.tipo}</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                          <div>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Frequenza</p>
                            <p className="text-xs text-foreground">{servizio.frequenza}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Durata</p>
                            <p className="text-xs text-foreground">{servizio.durata}</p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Provider</p>
                          <p className="text-xs text-primary font-medium">{servizio.provider}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic py-4 text-center">Nessun servizio attivo registrato.</p>
                )}
              </div>

              {/* Equipe UVMD Assegnata */}
              {(patient.medicoAssegnato || (patient.equipeAssegnata && patient.equipeAssegnata.length > 0)) && (
                <div className="bg-card rounded-xl border border-border shadow-card p-5">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-primary" />
                    Equipe Valutazione UVMD
                  </h3>
                  {patient.equipeAssegnata && patient.equipeAssegnata.length > 0 ? (
                    <div className="flex flex-col gap-1.5" data-testid="equipe-assegnata-list">
                      {patient.equipeAssegnata.map((member, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg border border-border/40" data-testid={`equipe-member-${i}`}>
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <User className="w-3 h-3 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-foreground truncate">{member.nome}</p>
                            <p className="text-[10px] text-muted-foreground truncate">{member.ruolo}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1.5" data-testid="equipe-assegnata-list">
                      {patient.medicoAssegnato.split(',').map((entry, i) => {
                        const trimmed = entry.trim();
                        const parts = trimmed.split(' - ');
                        const nome = parts[0] || trimmed;
                        const ruolo = parts[1] || '';
                        return (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg border border-border/40" data-testid={`equipe-member-${i}`}>
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <User className="w-3 h-3 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-foreground truncate">{nome}</p>
                              {ruolo && <p className="text-[10px] text-muted-foreground truncate">{ruolo}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {patient.dataVisitaUVMD && (
                    <p className="text-[11px] text-muted-foreground mt-3 pt-2 border-t border-border/40">Visita UVMD: {patient.dataVisitaUVMD}</p>
                  )}
                </div>
              )}

              {/* Action: Programma Rivalutazione */}
              <div className={`bg-card rounded-xl border shadow-card p-5 ${isPAIExpiring ? 'border-warning/40' : 'border-border'}`}>
                <Button
                  variant={isPAIExpiring ? 'default' : 'outline'}
                  size="lg"
                  className={`w-full gap-2 ${isPAIExpiring ? 'bg-warning text-warning-foreground hover:bg-warning/90' : ''}`}
                  onClick={handleRivalutazione}
                  data-testid="programma-rivalutazione-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Programma Rivalutazione
                </Button>
                {isPAIExpiring && (
                  <p className="text-[11px] text-muted-foreground text-center mt-3">
                    Avvia un nuovo ciclo di valutazione UVMD
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  /* ═══════════════════════════════════════
     ACCETTAZIONE VIEW (stato: da_accettare)
     ═══════════════════════════════════════ */
  return (
    <AppLayout role="segreteria">
      <div className="space-y-4">

        {/* ═══ BACK BUTTON (fuori dall'header, sullo sfondo pagina) ═══ */}
        <div className="flex items-center gap-2 mb-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/segreteria')} className="text-muted-foreground flex-shrink-0 w-8 h-8" data-testid="header-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground">Torna alla Dashboard</span>
        </div>

        {/* ═══ PATIENT PANEL HEADER ═══ */}
        <PatientPanelHeader
          patient={patient}
          onCall={handleCall}
          isCalling={isCalling}
          assignedProvider={assignedProvider}
          onViewMore={() => {
            const el = document.getElementById('modello-431-section');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
        />

        {/* ═══════════════════════════════════════════════
            SPLIT VIEW: 2-COLUMN LAYOUT
            Left (65%): Consultation Data
            Right (35%): Action Panel
            ═══════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] xl:grid-cols-[1fr_420px] gap-5 items-start">

          {/* ══════════ LEFT COLUMN: CONSULTATION ══════════ */}
          <div className="space-y-3 min-w-0">
            {/* Read-Only Banner — scoped to left column */}
            <div className="bg-muted/50 border border-border rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold text-foreground">Modello 431 — Modulo Unico di Attivazione PUA/CAD</span>
                <Badge variant="secondary" className="text-[9px]">Sola Lettura</Badge>
              </div>
              <Badge variant="chip" className="text-[9px]">ASL Roma | Regione Lazio</Badge>
            </div>

            <Accordion type="multiple" defaultValue={["anagrafica"]} className="space-y-3">

              {/* ── SEZ. 1: ANAGRAFICA ── */}
              <AccordionItem value="anagrafica" className="border rounded-xl bg-card shadow-card overflow-hidden">
                <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                      <User className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-foreground">Sezione 1 — Dati Anagrafici e Contesto</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <SectionHeading icon={User}>Dati del Paziente</SectionHeading>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mb-4">
                    <ReadOnlyField label="Cognome" value={m431.anagrafica.cognome} />
                    <ReadOnlyField label="Nome" value={m431.anagrafica.nome} />
                    <ReadOnlyField label="Data di Nascita" value={m431.anagrafica.dataNascita} />
                    <ReadOnlyField label="Luogo di Nascita" value={m431.anagrafica.luogoNascita} />
                    <ReadOnlyField label="Codice Fiscale" value={m431.anagrafica.codiceFiscale} />
                    <ReadOnlyField label="Telefono" value={m431.anagrafica.telefono} />
                    <ReadOnlyField label="Via" value={m431.anagrafica.via} />
                    <ReadOnlyField label="N. Civico" value={m431.anagrafica.civico} />
                    <ReadOnlyField label="CAP" value={m431.anagrafica.cap} />
                    <ReadOnlyField label="Comune" value={m431.anagrafica.comuneResidenza} />
                    <ReadOnlyField label="Municipio" value={m431.anagrafica.municipio} />
                    <ReadOnlyField label="Domicilio (se diverso)" value={m431.anagrafica.domicilio || "Coincide con residenza"} />
                  </div>
                  <Separator className="my-3" />
                  <SectionHeading icon={Heart}>Persona di Riferimento</SectionHeading>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
                    <ReadOnlyField label="Relazione" value={m431.personaRiferimento.relazione} />
                    <ReadOnlyField label="Cognome" value={m431.personaRiferimento.cognome} />
                    <ReadOnlyField label="Nome" value={m431.personaRiferimento.nome} />
                    <ReadOnlyField label="Telefono" value={m431.personaRiferimento.telefono} />
                  </div>
                  <Separator className="my-3" />
                  <SectionHeading icon={Stethoscope}>Medico Curante (MMG)</SectionHeading>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-4">
                    <ReadOnlyField label="Cognome" value={m431.mmg.cognome} />
                    <ReadOnlyField label="Nome" value={m431.mmg.nome} />
                    <ReadOnlyField label="Distretto" value={m431.mmg.distretto} />
                  </div>
                  <Separator className="my-3" />
                  <SectionHeading icon={Building2}>Provenienza Richiesta</SectionHeading>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {Object.entries(provenienzaLabels).map(([key, label]) => (
                      <ReadOnlyCheckbox key={key} label={label} checked={m431.provenienza === key} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ── SEZ. 2: VALUTAZIONE SANITARIA ── */}
              <AccordionItem value="sanitaria" className="border rounded-xl bg-card shadow-card overflow-hidden">
                <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                      <Stethoscope className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-foreground">Sezione 2 — Valutazione Sanitaria</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <SectionHeading icon={ClipboardList}>Diagnosi</SectionHeading>
                  <div className="space-y-2.5 mb-4">
                    <div>
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Diagnosi Principale</p>
                      <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40 leading-relaxed">{m431.diagnosi.principale}</div>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Concomitanti</p>
                      <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40 leading-relaxed">{m431.diagnosi.concomitanti}</div>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Codici ICD-9</p>
                      <div className="inline-flex flex-wrap gap-1.5">
                        {m431.diagnosi.codiciICD9.split(',').map((code, i) => (
                          <Badge key={i} variant="chip" className="text-xs font-mono">{code.trim()}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Evento acuto (90gg)?</p>
                      <RadioGroup value={m431.eventoAcuto90gg ? "si" : "no"} disabled className="flex gap-3">
                        <ReadOnlyRadio value="si" label="Sì" />
                        <ReadOnlyRadio value="no" label="No" />
                      </RadioGroup>
                    </div>
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Trasportabile?</p>
                      <RadioGroup value={m431.trasportabile ? "si" : "no"} disabled className="flex gap-3">
                        <ReadOnlyRadio value="si" label="Sì" />
                        <ReadOnlyRadio value="no" label="No" />
                      </RadioGroup>
                    </div>
                  </div>
                  <Separator className="my-3" />
                  <SectionHeading icon={Activity}>Condizioni Cliniche</SectionHeading>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 mb-4">
                    <ReadOnlyCheckbox label="Insuff. respiratoria" checked={m431.condizioniCliniche.insufficienzaRespiratoria} />
                    <ReadOnlyCheckbox label="Diabete" checked={m431.condizioniCliniche.diabete} />
                    <ReadOnlyCheckbox label="Cardiopatia" checked={m431.condizioniCliniche.cardiopatia} />
                    <ReadOnlyCheckbox label="Insuff. cardiaca" checked={m431.condizioniCliniche.insufficienzaCardiaca} />
                    <ReadOnlyCheckbox label="Malattie vascolari" checked={m431.condizioniCliniche.malattieVascolari} />
                    <ReadOnlyCheckbox label="Esiti pat. ortopediche" checked={m431.condizioniCliniche.esitiPatologieOrtopediche} />
                    <ReadOnlyCheckbox label="Mal. osteomuscolari" checked={m431.condizioniCliniche.malattieOsteomuscolari} />
                    <ReadOnlyCheckbox label="Esiti frattura femore" checked={m431.condizioniCliniche.esitiFratturaFemore} />
                    <ReadOnlyCheckbox label="Neurologiche" checked={m431.condizioniCliniche.neurologiche} />
                    <ReadOnlyCheckbox label="Mal. psichiatrica" checked={m431.condizioniCliniche.malattiaPsichiatrica} />
                    <ReadOnlyCheckbox label="Esiti di ictus" checked={m431.condizioniCliniche.esitiIctus} />
                    <ReadOnlyCheckbox label="Demenza" checked={m431.condizioniCliniche.demenza} />
                    <ReadOnlyCheckbox label="Disturbi sensoriali" checked={m431.condizioniCliniche.disturbiSensoriali} />
                    <ReadOnlyCheckbox label="Neoplasie" checked={m431.condizioniCliniche.neoplasie} />
                    <ReadOnlyCheckbox label="Altro" checked={m431.condizioniCliniche.altro} />
                  </div>
                  <Separator className="my-3" />
                  <SectionHeading icon={Pill}>Terapie in Atto</SectionHeading>
                  <div className="text-sm text-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40 leading-relaxed mb-4">{m431.terapiaInAtto || <span className="text-muted-foreground italic">—</span>}</div>
                  <Separator className="my-3" />
                  <SectionHeading>Condizioni Cliniche Attuali</SectionHeading>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Stabilità Clinica</p>
                      <RadioGroup value={m431.stabilitaClinica} disabled className="space-y-1">
                        <ReadOnlyRadio value="stabile" label="Stabile" />
                        <ReadOnlyRadio value="moderatamente_instabile" label="Mod. instabile" />
                        <ReadOnlyRadio value="instabile" label="Instabile" />
                      </RadioGroup>
                    </div>
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Stato di Coscienza</p>
                      <RadioGroup value={m431.statoCoscienza} disabled className="space-y-1">
                        <ReadOnlyRadio value="lucido" label="Lucido" />
                        <ReadOnlyRadio value="confuso" label="Confuso" />
                        <ReadOnlyRadio value="soporoso" label="Soporoso" />
                        <ReadOnlyRadio value="coma" label="Coma" />
                      </RadioGroup>
                    </div>
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Disturbi Cognitivi</p>
                      <RadioGroup value={m431.disturbiCognitivi} disabled className="flex flex-wrap gap-x-3 gap-y-1">
                        <ReadOnlyRadio value="assenti" label="Assenti" />
                        <ReadOnlyRadio value="lievi" label="Lievi" />
                        <ReadOnlyRadio value="moderati" label="Moderati" />
                        <ReadOnlyRadio value="gravi" label="Gravi" />
                      </RadioGroup>
                    </div>
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Disturbi Comportam.</p>
                      <RadioGroup value={m431.disturbiComportamentali} disabled className="flex flex-wrap gap-x-3 gap-y-1">
                        <ReadOnlyRadio value="assenti" label="Assenti" />
                        <ReadOnlyRadio value="lievi" label="Lievi" />
                        <ReadOnlyRadio value="moderati" label="Moderati" />
                        <ReadOnlyRadio value="gravi" label="Gravi" />
                      </RadioGroup>
                    </div>
                  </div>
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Scala del Dolore (0-10)</p>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-muted-foreground font-mono">0</span>
                      <Slider defaultValue={[m431.dolore]} max={10} step={1} disabled className="flex-1" />
                      <span className="text-[10px] text-muted-foreground font-mono">10</span>
                      <Badge variant={m431.dolore <= 3 ? 'success' : m431.dolore <= 6 ? 'warning' : 'alta'} className="text-[10px] font-mono min-w-[28px] justify-center">{m431.dolore}</Badge>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ── SEZ. 3: VALUTAZIONE SOCIALE ── */}
              <AccordionItem value="sociale" className="border rounded-xl bg-card shadow-card overflow-hidden">
                <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                      <Heart className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-foreground">Sezione 3 — Valutazione Sociale e Funzionale</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Mobilità</p>
                      <RadioGroup value={m431.mobilita} disabled className="space-y-1">
                        <ReadOnlyRadio value="indipendente" label="Indipendente" />
                        <ReadOnlyRadio value="aiuto_persona" label="Con aiuto" />
                        <ReadOnlyRadio value="totalmente_dipendente" label="Tot. dipendente" />
                      </RadioGroup>
                    </div>
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Alimentazione</p>
                      <RadioGroup value={m431.alimentazione} disabled className="space-y-1">
                        <ReadOnlyRadio value="autonoma" label="Autonoma" />
                        <ReadOnlyRadio value="assistito" label="Assistito" />
                        <ReadOnlyRadio value="rifiuta" label="Rifiuta" />
                        <ReadOnlyRadio value="artificiale" label="Artificiale" />
                      </RadioGroup>
                    </div>
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Vive con:</p>
                      <RadioGroup value={m431.supportoSociale} disabled className="space-y-1">
                        <ReadOnlyRadio value="solo" label="Solo" />
                        <ReadOnlyRadio value="coniuge" label="Coniuge" />
                        <ReadOnlyRadio value="con_figli" label="Figli" />
                        <ReadOnlyRadio value="genitori" label="Genitori" />
                      </RadioGroup>
                    </div>
                    <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Condizione Abitativa</p>
                      <RadioGroup value={m431.condizioneAbitativa} disabled className="space-y-1">
                        <ReadOnlyRadio value="idonea" label="Idonea" />
                        <ReadOnlyRadio value="parzialmente_idonea" label="Parz. Idonea" />
                        <ReadOnlyRadio value="non_idonea" label="Non Idonea" />
                      </RadioGroup>
                      {m431.barriereArchitettoniche && <p className="text-[10px] text-muted-foreground mt-1.5 italic">{m431.barriereArchitettoniche}</p>}
                    </div>
                  </div>
                  <SectionHeading icon={Activity}>Scala ADL</SectionHeading>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <ReadOnlyCheckbox label="Allettato" checked={m431.scalaADL.allettato} />
                    <ReadOnlyCheckbox label="Carrozzina" checked={m431.scalaADL.spostaCarrozzina} />
                    <ReadOnlyCheckbox label="Deambula con aiuto" checked={m431.scalaADL.deambulaConAiuto} />
                    <ReadOnlyCheckbox label="Confuso/Disorientato" checked={m431.scalaADL.confusoDisorientato} />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ── SEZ. 4: NECESSITÀ ASSISTENZIALI ── */}
              <AccordionItem value="percorso" className="border rounded-xl bg-card shadow-card overflow-hidden">
                <AccordionTrigger className="px-5 py-3.5 hover:no-underline hover:bg-muted/30">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-primary-muted flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-foreground">Sezione 4 — Necessità Assistenziali e Percorso</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <SectionHeading>Trattamenti e Procedure</SectionHeading>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 mb-4">
                    <ReadOnlyCheckbox label="Chemio/Radioterapia" checked={m431.trattamentiProcedure.chemioRadioterapia} />
                    <ReadOnlyCheckbox label="Ossigenoterapia (OLT)" checked={m431.trattamentiProcedure.ossigenoterapiaOLT} />
                    <ReadOnlyCheckbox label="Ventilazione meccanica" checked={m431.trattamentiProcedure.ventilazioneMeccanica} />
                    <ReadOnlyCheckbox label="Tracheostomia" checked={m431.trattamentiProcedure.tracheostomia} />
                    <ReadOnlyCheckbox label="Broncoaspirazione" checked={m431.trattamentiProcedure.broncoaspirazione} />
                    <ReadOnlyCheckbox label="Drenaggio posturale" checked={m431.trattamentiProcedure.drenaggioPosturale} />
                    <ReadOnlyCheckbox label="Dialisi" checked={m431.trattamentiProcedure.dialisi} />
                    <ReadOnlyCheckbox label="Trasfusioni" checked={m431.trattamentiProcedure.trasfusioniPeriodiche} />
                  </div>
                  <Separator className="my-3" />
                  <SectionHeading>Presidi e Farmaci</SectionHeading>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2 mb-4">
                    <ReadOnlyCheckbox label="Nutr. parenterale" checked={m431.somministrazioneFarmaci.nutrizioneParenterale} />
                    <ReadOnlyCheckbox label="Nutr. SNG" checked={m431.somministrazioneFarmaci.nutrizioneEnteraleSNG} />
                    <ReadOnlyCheckbox label="Nutr. PEG" checked={m431.somministrazioneFarmaci.nutrizioneEnteralePEG} />
                    <ReadOnlyCheckbox label="Catetere vescicale" checked={m431.somministrazioneFarmaci.catetereVescicale} />
                    <ReadOnlyCheckbox label="CVC o PICC" checked={m431.somministrazioneFarmaci.cvcPicc} />
                    <ReadOnlyCheckbox label="Enterostomia" checked={m431.somministrazioneFarmaci.enterostomia} />
                    <ReadOnlyCheckbox label="Ulcere cutanee" checked={m431.somministrazioneFarmaci.ulcereCutanee} />
                    <ReadOnlyCheckbox label="Terapia infusionale" checked={m431.somministrazioneFarmaci.terapiaInfusionale} />
                    <ReadOnlyCheckbox label="ECG periodici" checked={m431.somministrazioneFarmaci.ecgPeriodici} />
                    <ReadOnlyCheckbox label="Prelievi ematici" checked={m431.somministrazioneFarmaci.prelieviEmatici} />
                  </div>
                  <Separator className="my-3" />
                  <SectionHeading icon={FileText}>Percorso e Compilatore</SectionHeading>
                  <div className="p-2.5 bg-muted/30 rounded-lg border border-border/40 mb-3">
                    <RadioGroup value={m431.percorsoRichiesto} disabled className="grid grid-cols-2 lg:grid-cols-3 gap-1.5">
                      <ReadOnlyRadio value="riab_residenziale" label="Riab. Resid." />
                      <ReadOnlyRadio value="riab_semiresidenziale" label="Riab. Semires." />
                      <ReadOnlyRadio value="riab_domiciliare" label="Riab. Domic." />
                      <ReadOnlyRadio value="adi" label="ADI" />
                      <ReadOnlyRadio value="rsa" label="RSA" />
                    </RadioGroup>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                    <ReadOnlyField label="Ruolo" value={m431.compilatore.ruolo === 'mmg' ? 'MMG' : 'Medico Osped.'} />
                    <ReadOnlyField label="Nominativo" value={`${m431.compilatore.cognome} ${m431.compilatore.nome}`} />
                    <ReadOnlyField label="Telefono" value={m431.compilatore.telefono} />
                    <ReadOnlyField label="Data" value={m431.compilatore.data} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* ══════════ RIGHT COLUMN: ACTION PANEL ══════════ */}
          <div className="lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto flex flex-col gap-6">

            {/* ── LIVELLO DI COMPLESSITÀ (READ-ONLY) ── */}
            <div className="bg-card rounded-xl border border-border shadow-card p-4" data-testid="complessita-panel">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-primary" />
                Livello di Complessità
              </h3>
              <p className="text-[11px] text-muted-foreground mb-3">
                Valutata dal PUA/MMG: <span className="font-semibold text-foreground capitalize">{patient.priorita}</span>
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'bassa', label: 'Bassa', sub: 'U-C · 10gg', dotClass: 'bg-success' },
                  { key: 'media', label: 'Media', sub: 'U-B · 72h', dotClass: 'bg-warning' },
                  { key: 'alta', label: 'Alta', sub: 'U-A · 24h', dotClass: 'bg-destructive' },
                ].map((urg) => {
                  const isActive = patient.priorita === urg.key;
                  return (
                    <div
                      key={urg.key}
                      data-testid={`complessita-btn-${urg.key}`}
                      className={`flex flex-col items-center p-3 rounded-xl border-2 text-center cursor-default select-none ${
                        isActive
                          ? urg.key === 'alta' ? 'border-destructive bg-destructive/8'
                            : urg.key === 'media' ? 'border-warning bg-warning/8'
                            : 'border-success bg-success/8'
                          : 'border-border bg-muted/20 opacity-40'
                      }`}
                    >
                      <span className={`w-2.5 h-2.5 rounded-full ${urg.dotClass} mb-1.5 ${isActive ? '' : 'opacity-30'}`} />
                      <span className={`text-sm font-bold leading-none ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{urg.label}</span>
                      <span className={`text-[9px] mt-1 ${isActive ? 'text-foreground/60' : 'text-muted-foreground/50'}`}>{urg.sub}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── EQUIPE UVMD (solo complessità media/alta) ── */}
            {(patient.priorita === 'media' || patient.priorita === 'alta') ? (
            <>
            <div className="bg-card rounded-xl border border-border shadow-card p-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-primary" />
                Equipe Valutazione UVMD
              </h3>
              <p className="text-[11px] text-muted-foreground mb-3">Seleziona le figure professionali (ruoli)</p>

              {/* Dropdown Multi-Select (custom, no Radix Popover) */}
              <div className="relative" ref={equipeDropdownRef}>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={equipeOpen}
                  className="w-full justify-between h-auto min-h-[40px] font-normal text-sm"
                  data-testid="equipe-dropdown-trigger"
                  onClick={() => setEquipeOpen(prev => !prev)}
                >
                  <span className={selectedEquipe.length === 0 ? 'text-muted-foreground' : 'text-foreground'}>
                    {selectedEquipe.length === 0
                      ? 'Cerca o seleziona ruolo...'
                      : `${selectedEquipe.length} ruol${selectedEquipe.length === 1 ? 'o' : 'i'} selezionat${selectedEquipe.length === 1 ? 'o' : 'i'}`}
                  </span>
                  <ChevronsUpDown className="w-4 h-4 text-muted-foreground flex-shrink-0 ml-2" />
                </Button>
                {equipeOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    <div className="px-3 py-2 border-b border-border">
                      <input
                        type="text"
                        placeholder="Cerca ruolo..."
                        value={equipeSearch}
                        onChange={(e) => setEquipeSearch(e.target.value)}
                        className="w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground text-foreground"
                        data-testid="equipe-search-input"
                      />
                    </div>
                    {[{ key: 'base', heading: 'Nucleo Base (Obbligatori)' }, { key: 'specialisti', heading: 'Specialisti (Attivabili al bisogno)' }].map(group => {
                      const filtered = equipeProfessionals.filter(p => p.group === group.key && p.ruolo.toLowerCase().includes(equipeSearch.toLowerCase()));
                      if (filtered.length === 0) return null;
                      return (
                        <div key={group.key}>
                          <p className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30">{group.heading}</p>
                          {filtered.map(prof => {
                            const isSelected = selectedEquipe.includes(prof.id);
                            return (
                              <button
                                key={prof.id}
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors text-left cursor-pointer"
                                onClick={() => handleEquipeToggle(prof.id)}
                                data-testid={`equipe-option-${prof.id}`}
                              >
                                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-primary border-primary' : 'border-border'}`}>
                                  {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                                </div>
                                <span className="text-foreground">{prof.ruolo}</span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Removable Chips — sempre solo ruolo */}
              {selectedEquipe.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {selectedEquipe.map(id => {
                    const prof = equipeProfessionals.find(p => p.id === id);
                    if (!prof) return null;
                    return (
                      <Badge
                        key={id}
                        variant="chip"
                        className="text-[11px] pl-2.5 pr-1 py-1 gap-1 cursor-default max-w-full"
                        data-testid={`equipe-chip-${id}`}
                      >
                        <span className="truncate">{prof.ruolo}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEquipeToggle(id); }}
                          className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                          data-testid={`equipe-chip-remove-${id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── 3. CALENDARIO (Conditional) ── */}
            <AnimatePresence>
              {selectedEquipe.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="bg-card rounded-xl border border-border shadow-card p-4">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-1">
                      <CalendarDays className="w-4 h-4 text-primary" />
                      Calendario disponibilità
                    </h3>
                    <p className="text-[11px] text-muted-foreground mb-3">
                      Incrociati per {selectedEquipe.length} figure
                    </p>

                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => { setSelectedDate(d); setSelectedSlot(null); }}
                      locale={it}
                      weekStartsOn={1}
                      className="rounded-lg border border-border w-full [&_.rdp-months]:w-full [&_.rdp-month]:w-full [&_.rdp-table]:w-full"
                      disabled={(date) => date < new Date() || !isDateAvailable(date)}
                      modifiers={{ available: availableDates }}
                      modifiersClassNames={{ available: 'cal-day-available' }}
                      classNames={{
                        day_disabled: 'cal-day-unavailable',
                      }}
                    />

                    {/* Time Slots */}
                    {selectedDate && slotsForSelectedDate.length > 0 && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                          {selectedDate.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {slotsForSelectedDate.map((slot) => {
                            const isActive = selectedSlot === slot;
                            return (
                              <button
                                key={slot}
                                onClick={() => setSelectedSlot(slot)}
                                className={`flex items-center justify-center gap-1 px-2 py-2 rounded-lg border text-xs font-semibold transition-all duration-200 ${
                                  isActive
                                    ? 'border-primary bg-primary text-primary-foreground shadow-comfort'
                                    : 'border-border bg-card text-foreground hover:border-primary/30 hover:bg-primary-muted'
                                }`}
                              >
                                <Clock className="w-3 h-3" />
                                {slot}
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── 3b. EQUIPE ASSEGNATA (Fase 3: Nomi rivelati post-slot) ── */}
            <AnimatePresence>
              {namesRevealed && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="bg-primary-muted/30 rounded-xl border border-primary/20 shadow-card p-4" data-testid="equipe-names-revealed">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      Equipe Individuata
                    </h3>
                    {/* Data e orario selezionati */}
                    <div className="flex items-center gap-2 px-3 py-2.5 mb-3 bg-card rounded-lg border border-primary/15" data-testid="equipe-datetime-summary">
                      <CalendarIcon className="w-4 h-4 text-primary flex-shrink-0" />
                      <p className="text-xs font-medium text-foreground">
                        Visita UVMD programmata per: <span className="font-semibold">{selectedDate?.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</span> alle ore <span className="font-semibold">{selectedSlot}</span>
                      </p>
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-2">Professionisti assegnati:</p>
                    <div className="flex flex-col gap-1.5">
                      {selectedEquipe.map(id => {
                        const prof = equipeProfessionals.find(p => p.id === id);
                        if (!prof) return null;
                        return (
                          <div key={id} className="flex items-center gap-2 px-3 py-2 bg-card rounded-lg border border-border/50" data-testid={`equipe-assigned-${id}`}>
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <User className="w-3 h-3 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-foreground truncate">{prof.nome}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{prof.ruolo}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── 4. CTA: ACCEPT BUTTON (Flusso UVMD) ── */}
            <div className={`bg-card rounded-xl border shadow-card p-4 transition-all duration-300 lg:sticky lg:bottom-0 ${
              canAccept ? 'border-primary/20' : 'border-border opacity-80'
            }`}>
              <Button
                variant="comfort"
                size="lg"
                className="w-full gap-2"
                onClick={handleAccept}
                disabled={!canAccept || isAccepting || patient.stato !== 'da_accettare'}
                data-testid="accept-btn"
              >
                {isAccepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {isAccepting ? 'Salvataggio...' : patient.stato !== 'da_accettare' ? 'Gia Accettata' : 'Accetta e Attiva Richiesta'}
              </Button>
            </div>
            </>
            ) : (
            <>
            {/* ── FLUSSO BASSA COMPLESSITÀ ── */}
            <div className="bg-card rounded-xl border border-border shadow-card p-4" data-testid="bassa-complessita-panel">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-1">
                <Building2 className="w-4 h-4 text-primary" />
                Assegnazione Provider (Bassa Complessità)
              </h3>
              <p className="text-[11px] text-muted-foreground mb-4">
                La richiesta a bassa complessità non necessita di valutazione UVMD. Seleziona l'erogatore convenzionato per l'attivazione diretta.
              </p>

              {assignedProvider ? (
                /* ── Post-assegnazione: Alert "In attesa" ── */
                <div className="space-y-3" data-testid="provider-assigned-alert">
                  <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          Provider Assegnato: {assignedProvider.nome}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          In attesa di accettazione da parte dell'erogatore.
                        </p>
                        <div className="flex items-center gap-1.5 mt-2">
                          <Clock className="w-3 h-3 text-warning" />
                          <p className="text-[11px] text-warning font-medium">
                            L'erogatore ha 24 ore di tempo per prendere in carico la richiesta.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg border border-border/40 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Dettagli Provider</p>
                      <p className="text-xs text-foreground mt-0.5">{assignedProvider.specializzazioni}</p>
                    </div>
                    <span className="text-[10px] font-mono text-muted-foreground border border-border rounded-full px-3 py-1 whitespace-nowrap w-fit flex-shrink-0">{assignedProvider.distanza}</span>
                  </div>
                </div>
              ) : (
                /* ── Pre-assegnazione: pulsante ── */
                <div className="p-3 bg-muted/30 rounded-lg border border-border/40">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Prossimo step</p>
                  <p className="text-xs text-foreground">Seleziona il provider convenzionato e attiva le prestazioni a calendario.</p>
                </div>
              )}
            </div>

            {!assignedProvider && (
              <div className="bg-card rounded-xl border border-primary/20 shadow-card p-4 transition-all duration-300 lg:sticky lg:bottom-0">
                <Button
                  variant="comfort"
                  size="lg"
                  className="w-full gap-2"
                  onClick={() => setProviderModalOpen(true)}
                  disabled={patient.stato !== 'da_accettare'}
                  data-testid="assegna-provider-btn"
                >
                  <Building2 className="w-4 h-4" />
                  Assegna provider
                </Button>
              </div>
            )}

            {/* ═══ MODALE "Seleziona Erogatore" ═══ */}
            <AnimatePresence>
              {providerModalOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4"
                  data-testid="provider-modal-backdrop"
                  onClick={() => setProviderModalOpen(false)}
                >
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="provider-modal"
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between p-5 border-b border-border">
                      <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
                        <Building2 className="w-4.5 h-4.5 text-primary" />
                        Assegna Erogatore Convenzionato
                      </h2>
                      <button
                        onClick={() => setProviderModalOpen(false)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
                        data-testid="provider-modal-close"
                      >
                        <X className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>

                    {/* Body */}
                    <div className="px-5 py-6 overflow-y-auto flex-1 flex flex-col gap-6">
                      {providerConvenzionati.map((prov) => {
                        const isSelected = selectedProvider === prov.id;
                        return (
                          <button
                            key={prov.id}
                            type="button"
                            className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                              isSelected
                                ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                                : 'border-border bg-card hover:border-primary/20 hover:bg-muted/20'
                            }`}
                            onClick={() => setSelectedProvider(prov.id)}
                            data-testid={`provider-option-${prov.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                                isSelected ? 'border-primary bg-primary' : 'border-border'
                              }`}>
                                {isSelected && <div className="w-2 h-2 rounded-full bg-primary-foreground" />}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-foreground">{prov.nome}</p>
                                <p className="text-[11px] text-muted-foreground mt-0.5">{prov.specializzazioni}</p>
                                <div className="flex items-center gap-3 mt-1.5">
                                  <span className="text-[10px] text-muted-foreground">Rating operatore: <span className="font-semibold text-foreground">{prov.punteggio}/100</span></span>
                                  <span className="text-[10px] text-muted-foreground">Distanza: <span className="font-semibold text-foreground">{prov.distanza}</span></span>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
                      <Button
                        variant="outline"
                        onClick={() => { setProviderModalOpen(false); setSelectedProvider(null); }}
                        data-testid="provider-modal-cancel"
                      >
                        Annulla
                      </Button>
                      <Button
                        variant="comfort"
                        disabled={!selectedProvider}
                        onClick={handleConfirmProvider}
                        className="gap-2"
                        data-testid="provider-modal-confirm"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Conferma Assegnazione
                      </Button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            </>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
